import { useMemo, useId } from 'react';
import { useTheme } from "@mui/material";
import {
  ChartContainer,
  LinePlot,
  MarkPlot,
  ScatterPlot,
  ChartsReferenceLine,
  ChartsXAxis,
  ChartsYAxis,
  ChartsAxisHighlight,
  ChartsTooltip,
  useDrawingArea,
  useYScale,
} from "@mui/x-charts";

// Inner component that has access to chart context (drawing area + scales)
function ReferenceZone({ lowerReference, upperReference }) {
  const { left, top, width, height } = useDrawingArea();
  const yScale = useYScale();
  const clipId = useId();

  if (!Number.isFinite(lowerReference) || !Number.isFinite(upperReference)) {
    return null;
  }

  const yTop    = yScale(upperReference);
  const yBottom = yScale(lowerReference);

  // Clamp to the drawing area boundaries so the band never bleeds outside the chart
  const bandTop    = Math.max(top, Math.min(yTop, top + height));
  const bandBottom = Math.max(top, Math.min(yBottom, top + height));
  const bandHeight = bandBottom - bandTop;

  if (bandHeight <= 0) return null;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={left} y={top} width={width} height={height} />
        </clipPath>
      </defs>
      <rect
        x={left}
        y={bandTop}
        width={width}
        height={bandHeight}
        fill="rgba(34, 197, 94, 0.15)"   // light green, semi-transparent
        clipPath={`url(#${clipId})`}
        pointerEvents="none"
      />
    </g>
  );
}

export default function BloodLineChart({
  id,
  unit,
  data,
  xLabels,
  yWidth = 50,
  lowerReference,
  upperReference,
  margin = { left: 10, right: 60, bottom: 10, top: 20 },
  width = 550,
  height = 300,
  latestPoint,
  latestColor,
}) {
  const theme = useTheme();

  const lineColor = theme.palette.mode === "light"
    ? theme.palette.grey[500]
    : theme.palette.grey[300];
  const pointColor = latestColor || theme.palette.primary.main;
  const referenceColor = theme.palette.mode === "light"
    ? theme.palette.grey[700]
    : theme.palette.grey[300];

  const minY = Math.min(...data, lowerReference ?? Infinity);
  const maxY = Math.max(...data, upperReference ?? -Infinity);
  const yPadding = (maxY - minY) * 0.1 || 1;
  const yDomain = [minY - yPadding, maxY + yPadding];

  const series = useMemo(() => {
    const base = [
      {
        id: id || "measurement",
        type: "line",
        data,
        color: lineColor,
        label: "Measurements",
        showMark: true,
        highlightScope: { highlighted: "item", faded: "global" },
        valueFormatter: (v) => (v == null ? "" : `${v} ${unit}`),
      },
    ];

    if (latestPoint?.length) {
      base.push({
        id: "latest",
        type: "scatter",
        data: latestPoint,
        color: pointColor,
        markerSize: 8,
        label: "Latest",
        valueFormatter: (v) => (v == null ? "" : `${v} ${unit}`),
      });
    }

    return base;
  }, [data, id, latestPoint, unit, lineColor, pointColor]);

  return (
    <ChartContainer
      width={width}
      height={height}
      series={series}
      xAxis={[
        {
          scaleType: "point",
          data: xLabels,
          valueFormatter: (v) =>
            v instanceof Date ? v.toLocaleDateString() : String(v),
          tickLabelStyle: {
            fill: theme.palette.text.secondary,
            textAnchor: "end",
            transform: "rotate(-45deg)",
          },
        },
      ]}
      yAxis={[
        {
          width: yWidth,
          min: yDomain[0],
          max: yDomain[1],
          tickLabelStyle: { fill: theme.palette.text.secondary },
        },
      ]}
      margin={margin}
    >
      {/* Reference zone band — rendered before the line so it sits behind */}
      <ReferenceZone
        lowerReference={lowerReference}
        upperReference={upperReference}
      />

      <LinePlot />
      <MarkPlot />
      {latestPoint?.length ? <ScatterPlot /> : null}

      {Number.isFinite(lowerReference) && (
        <ChartsReferenceLine
          y={lowerReference}
          label="Lower Ref"
          labelAlign="start"
          lineStyle={{
            stroke: referenceColor,
            strokeDasharray: "4 4",
          }}
        />
      )}

      {Number.isFinite(upperReference) && (
        <ChartsReferenceLine
          y={upperReference}
          label="Upper Ref"
          labelAlign="start"
          lineStyle={{
            stroke: referenceColor,
            strokeDasharray: "4 4",
          }}
        />
      )}

      <ChartsXAxis />
      <ChartsYAxis />
      <ChartsAxisHighlight x="line" stroke={theme.palette.divider} />
      <ChartsTooltip
        trigger="axis"
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        }}
      />
    </ChartContainer>
  );
}